import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Wallet,
  Receipt,
  Gift,
  CreditCard,
  Heartbeat,
  Buildings,
  ChartLineUp,
  Users,
  Gear,
  Brain,
  Calculator,
  CheckCircle,
  Warning,
  CurrencyCircleDollar,
  Eye,
  Hospital,
  Stethoscope,
  Flask,
  Pill,
  CaretDown,
  Lightning,
  TrendDown,
  ArrowsClockwise,
  Pulse,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Layout } from "../components/layout/Layout";
import {
  LiquidGlassBg,
  MacbookFrame,
  IphoneFrame,
} from "../components/ui/LiquidGlassComponents";
import OrbitalBenefitRails from "../components/sections/OrbitalBenefitRails";
import pk from "../assets/pakistan.png";
import employeeAppImg from "../assets/images/employee-app.png";
import architectureDashboardImg from "../assets/images/architecture-dashboard.png";
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

// Hero Section - Left Aligned as per design spec
// Hero Section - Left Aligned as per design spec
function HeroSection() {
  const [featureIdx, setFeatureIdx] = React.useState(0);
  const features = [
    "Design your healthcare model.",
    "Control your costs.",
    "Scale without complexity.",
  ];

  React.useEffect(() => {
    const timer = setInterval(() => {
      setFeatureIdx((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [features.length]);

  return (
    <section className="relative min-h-[90vh] lg:h-screen flex items-center overflow-hidden bg-white pt-20 lg:pt-0">
      <LiquidGlassBg />

      <div className="container-full relative z-10 mt-12 lg:mt-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-8">
          {/* Left Column: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-full space-y-6 sm:space-y-8 z-20"
          >
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#0048ff] text-[10px] sm:text-xs font-bold uppercase tracking-widest border border-blue-100">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0048ff] animate-pulse" />
                HEALTH FINTECH INFRASTRUCTURE
              </span>
            </div>

            <h1 className="text-[40px] sm:text-[56px] lg:text-[64px] xl:text-[72px] font-black leading-[1.1] tracking-tight text-[#11111f]">
              <span className="block">Corporate Health</span>
              <span className="text-[#0048ff] block">
                Benefit Infrastructure
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-[#11111f]/70 max-w-xl font-medium leading-relaxed">
              One platform. Complete control. Total flexibility.
              <br className="hidden sm:block" />
              <span className="text-[#11111f]/50 font-normal">
                A modular healthcare system built for modern employers.
              </span>
            </p>

            {/* List 1 - Strategic Capabilities - ROLLING TEXT */}
            <div className="h-10 sm:h-12 overflow-hidden relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={featureIdx}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="flex items-center gap-3 absolute inset-0"
                >
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#0048ff]/10 flex items-center justify-center shrink-0">
                    <CheckCircle
                      size={16}
                      weight="fill"
                      className="text-[#0048ff]"
                    />
                  </div>
                  <span className="text-[#11111f] font-semibold text-base sm:text-xl italic">
                    {features[featureIdx]}
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo">
                <button className="w-full sm:w-auto px-8 py-4 bg-[#0048ff] text-white rounded-2xl font-bold text-lg shadow-xl shadow-blue-200 hover:bg-[#003bce] transition-all flex items-center justify-center gap-2">
                  Book a Demo <ArrowRight size={20} weight="bold" />
                </button>
              </Link>
              <Link to="/register">
                <button className="w-full sm:w-auto px-8 py-4 bg-white text-[#0048ff] border-2 border-[#0048ff] rounded-2xl font-bold text-lg hover:bg-blue-50 transition-all flex items-center justify-center">
                  Register Your Company
                </button>
              </Link>
            </div>

            {/* Feature Badges */}
            <div className="pt-8 border-t border-slate-100 flex flex-wrap gap-6 mt-8">
              {[
                { label: "Cost visibility", icon: Eye },
                { label: "Modular design", icon: Gear },
                { label: "Real-time governance", icon: ShieldCheck },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <item.icon
                    size={18}
                    weight="bold"
                    className="text-[#0048ff]"
                  />
                  <span className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Column: Device Mockups - HIDDEN ON MOBILE */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            className="hidden lg:flex relative h-[600px] items-center justify-center"
          >
            {/* MacBook Mockup */}
            <MacbookFrame className="w-[800px] xl:w-[900px] transition-transform duration-1000 ease-out shadow-[0_40px_80px_-20px_rgba(0,0,0,0.3)]">
              <div className="relative w-full h-full bg-slate-50">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/aa088ff0-7e57-49b4-bb3e-5cf2e53229c2/images/b5ffd6abc2777059bb1963eb7588fae991e8c3f9fd89954df86c6b76e03bdd02.png"
                  alt="Mentor Health Dashboard"
                  className="w-full h-full object-cover"
                />
              </div>
            </MacbookFrame>

            {/* iPhone Mockup - Smaller and moved slightly right */}
            <motion.div
              initial={{ y: 50, opacity: 0, rotate: -15 }}
              animate={{ y: 0, opacity: 1, rotate: -8 }}
              transition={{ delay: 0.8, duration: 0.6, ease: "easeOut" }}
              className="absolute bottom-[5%] left-[10%] xl:left-[8%] w-[200px] xl:w-[240px] z-30 drop-shadow-[0_20px_50px_rgba(0,0,0,0.3)] hover:rotate-0 transition-transform duration-500"
            >
              <IphoneFrame>
                <div className="bg-[#0048ff] p-6 text-white h-full flex flex-col relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
                  <div className="flex justify-between items-start mb-10 z-10">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                      <Wallet size={20} weight="bold" />
                    </div>
                    <div className="px-3 py-1 rounded-full bg-white/20 text-[10px] font-bold uppercase backdrop-blur-md">
                      Pro Plan
                    </div>
                  </div>
                  <div className="z-10 mb-8">
                    <p className="text-xs uppercase tracking-widest opacity-60 mb-2">
                      Available Balance
                    </p>
                    <h3 className="text-3xl font-bold">$12,450.00</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3 z-10">
                    {[
                      { label: "Hospital", icon: Hospital },
                      { label: "Pharmacy", icon: Pill },
                      { label: "Lab Test", icon: Flask },
                      { label: "Checkup", icon: Stethoscope },
                    ].map((btn, i) => (
                      <div
                        key={i}
                        className="bg-white/10 backdrop-blur-md rounded-xl p-4 flex flex-col items-center gap-2 border border-white/10"
                      >
                        <btn.icon size={20} />
                        <span className="text-[10px] font-medium">
                          {btn.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-auto z-10">
                    <button className="w-full bg-white text-[#0048ff] py-4 rounded-2xl font-bold text-sm shadow-xl">
                      Find a Hospital
                    </button>
                  </div>
                </div>
              </IphoneFrame>
            </motion.div>

            {/* Savings Badge */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-10 -right-5 xl:right-5 liquid-glass p-5 rounded-2xl shadow-2xl z-40 flex items-center gap-4 min-w-[200px]"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-600">
                <ChartLineUp size={28} weight="duotone" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                  Savings Velocity
                </p>
                <h4 className="text-xl font-bold text-slate-900">+42.5%</h4>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// What Is Section - High Fidelity Mission
function WhatIsSection() {
  return (
    <section className="bg-white py-32 relative overflow-hidden" id="about">
      {/* Background Ornaments */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#0048ff]/5 rounded-full blur-[120px] translate-x-1/3 -translate-y-1/3 opacity-50" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[100px] -translate-x-1/3 translate-y-1/3 opacity-30" />

      <div className="container-wide relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-20 lg:gap-32">
          {/* Left Content - Value Prop */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:w-1/2"
          >
            <span className="label-text mb-6 block text-[#0048ff] font-bold tracking-widest uppercase">
              What We Are
            </span>
            <h2 className="text-[42px] md:text-[56px] font-bold mb-8 leading-[1.05] tracking-tight text-[#11111f]">
              Health Fintech <br />
              <span className="text-[#0048ff]">Infrastructure</span> <br />
              as a Service
            </h2>
            <p className="text-xl text-[#11111f]/70 leading-relaxed mb-12 max-w-xl font-medium">
              We build the autonomous, AI-driven infrastructure that empowers
              employers to finance, govern, and optimize healthcare at scale.
            </p>

            <div className="space-y-8">
              {[
                {
                  title: "Redefining Financing",
                  desc: "Move from rigid premiums to transparent, data-driven funding model.",
                  icon: CurrencyCircleDollar,
                },
                {
                  title: "Autonomous Governance",
                  desc: "Real-time AI utilization control that eliminates financial leakage.",
                  icon: ShieldCheck,
                },
                {
                  title: "Unified Ecosystem",
                  desc: "Connects Patients, Providers, and Payers on a single data rail.",
                  icon: ArrowsClockwise,
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-5 group">
                  <div className="w-14 h-14 rounded-2xl bg-[#F8F9FB] flex items-center justify-center shrink-0 border border-[#E5E7EB] group-hover:bg-[#0048ff] group-hover:text-white transition-all duration-300">
                    <item.icon size={28} weight="duotone" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-[#11111f] mb-1">
                      {item.title}
                    </h4>
                    <p className="text-[#11111f]/50 leading-relaxed max-w-sm">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Global Footprint Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="lg:w-1/2 relative"
          >
            <div className="liquid-glass p-8 md:p-14 rounded-[48px] relative z-10 border border-white/20 shadow-2xl bg-white/40">
              <div className="flex items-center gap-6 mb-16">
                <div className="w-16 h-16 rounded-2xl bg-[#0048ff] flex items-center justify-center text-white shadow-xl shadow-[#0048ff]/30">
                  <Buildings size={32} weight="duotone" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#11111f]">
                    Global Standards
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <img
                      src={pk}
                      alt="Pakistan"
                      className="w-5 h-5 object-contain"
                    />
                    <p className="text-[#11111f]/40 uppercase text-[10px] tracking-widest font-bold">
                      SECP Licensed TPA
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 md:gap-6">
                {[
                  { city: "Singapore", role: "Financial HQ" },
                  { city: "Pakistan", role: "Operations Hub" },
                  { city: "UAE", role: "Region Growth" },
                  { city: "USA", role: "Technology R&D" },
                ].map((loc, i) => (
                  <div
                    key={i}
                    className="p-6 rounded-3xl bg-white/60 backdrop-blur-md border border-white/40 hover:border-[#0048ff]/20 hover:bg-white transition-all group"
                  >
                    <h4 className="font-bold text-[#11111f] group-hover:text-[#0048ff] transition-colors">
                      {loc.city}
                    </h4>
                    <p className="text-[10px] uppercase font-bold tracking-widest text-[#11111f]/30 mt-1">
                      {loc.role}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-16 p-8 bg-[#0A1128] rounded-[32px] text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0048ff]/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 mb-3">
                      Shariah Compliant
                    </p>
                    <h4 className="text-xl font-bold">Validated Framework</h4>
                  </div>
                  <Lightning
                    size={32}
                    weight="fill"
                    className="text-[#0048ff]"
                  />
                </div>
              </div>
            </div>

            {/* Decorative Floating Elements */}
            <motion.div
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-12 -left-12 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"
            />
            <motion.div
              animate={{ x: [0, 15, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-10 -right-10 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Problem Section - Interactive Accordion
function ProblemSection() {
  const problems = [
    {
      title: "Fragmented Ecosystem",
      desc: "Today, healthcare is fragmented. Insurance is handled separately, claims are processed elsewhere, wellness is disconnected, and reimbursements remain manual. Every vendor operates in isolation.",
      solution:
        "Mentor's unified infrastructure connects all health modules into one autonomous data engine.",
    },
    {
      title: "Operational Blindness",
      desc: "The result of fragmentation: multiple portals, contracts, and reports. There is no unified dashboard, no cost visibility, and zero predictive analytics.",
      solution:
        "One real-time dashboard provides absolute visibility and control over global healthcare spend.",
    },
    {
      title: "Leadership Gap",
      desc: "HR coordinates, Finance reconciles, but Leadership operates blind. Manual processes create massive financial leakage and administrative overhead.",
      solution:
        "Automated digital settlements and rule-based governance eliminate manual cycles and hidden costs.",
    },
  ];

  const [activeProblem, setActiveProblem] = React.useState(0);

  return (
    <section className="relative py-32 overflow-hidden bg-white" id="problem">
      <div className="container-wide relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="label-text mb-4 block text-red-500 font-bold uppercase tracking-[0.2em]">
              The Problem
            </span>
            <h2 className="heading-section mb-8 text-left mx-0">
              Corporate Healthcare Is
              <br />
              <span className="text-red-500 underline decoration-2 underline-offset-8">
                Structurally Broken
              </span>
            </h2>
            <div className="space-y-4">
              {problems.map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => setActiveProblem(idx)}
                  className={`p-6 rounded-2xl border-2 transition-all cursor-pointer ${
                    activeProblem === idx
                      ? "border-red-500 bg-red-500/[0.02] shadow-lg shadow-red-500/5"
                      : "border-[#E5E7EB] hover:border-red-500/40"
                  }`}
                  style={
                    activeProblem === idx
                      ? {
                          backgroundColor: "rgba(239, 68, 68, 0.02)",
                          backdropFilter: "blur(10px)",
                        }
                      : {}
                  }
                >
                  <div className="flex justify-between items-center mb-2">
                    <h3
                      className={`text-lg font-bold transition-all ${activeProblem === idx ? "text-red-600" : "text-[#11111f]"}`}
                    >
                      {item.title}
                    </h3>
                    <motion.div
                      animate={{ rotate: activeProblem === idx ? 180 : 0 }}
                    >
                      <CaretDown
                        size={20}
                        weight="bold"
                        className={
                          activeProblem === idx
                            ? "text-red-500"
                            : "text-[#11111f]/40"
                        }
                      />
                    </motion.div>
                  </div>
                  <motion.div
                    initial={false}
                    animate={{
                      height: activeProblem === idx ? "auto" : 0,
                      opacity: activeProblem === idx ? 1 : 0,
                    }}
                    className="overflow-hidden"
                  >
                    <p className="text-[#11111f]/60 mb-4 leading-relaxed">
                      {item.desc}
                    </p>
                    <div className="liquid-glass-dark text-white p-5 rounded-xl text-sm font-medium flex gap-4 border border-white/10">
                      <div className="w-8 h-8 rounded-lg bg-[#0048ff]/20 flex items-center justify-center shrink-0">
                        <Lightning
                          size={18}
                          weight="fill"
                          className="text-[#0048ff]"
                        />
                      </div>
                      <span className="leading-relaxed">{item.solution}</span>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="relative">
            <motion.div
              key={activeProblem}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="liquid-glass rounded-[48px] p-12 border border-[#E5E7EB] aspect-square flex items-center justify-center relative overflow-hidden shadow-2xl bg-white/40"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-red-500/10 via-transparent to-transparent" />
              <div className="text-center relative z-10 w-full">
                <div className="w-24 h-24 bg-white/80 backdrop-blur-md rounded-[32px] shadow-2xl flex items-center justify-center mx-auto mb-10 text-red-500 border border-red-500/20">
                  {activeProblem === 0 && (
                    <Warning size={48} weight="duotone" />
                  )}
                  {activeProblem === 1 && (
                    <Receipt size={48} weight="duotone" />
                  )}
                  {activeProblem === 2 && <Eye size={48} weight="duotone" />}
                </div>
                <h4 className="text-3xl font-bold mb-4 tracking-tight text-[#11111f]">
                  Financial Inefficiency
                </h4>
                <p className="text-[#11111f]/40 font-semibold text-lg">
                  Mentor Health resolves fragmentation <br /> through
                  infrastructure-first design.
                </p>

                {/* Visual data hint */}
                <div className="mt-12 flex justify-center gap-3">
                  {[40, 70, 45, 90, 60].map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      animate={{ height: h }}
                      className="w-4 bg-red-500/30 rounded-full self-end border border-red-500/10"
                      transition={{ delay: 0.3 + i * 0.1 }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Employee Reality Section - The User Experience
function EmployeeRealitySection() {
  const steps = [
    {
      title: "Mobile-First Access",
      desc: "Instant digital card and hospital search directly on the phone.",
      icon: CreditCard,
    },
    {
      title: "Instant Approvals",
      desc: "No more waiting hours for insurance clearance. Everything is autonomous.",
      icon: Lightning,
    },
    {
      title: "Real-time Navigation",
      desc: "Smart guidance to the nearest preferred network provider.",
      icon: Stethoscope,
    },
    {
      title: "$0 Upfront Cost",
      desc: "Direct settlements at preferred networks means no out-of-pocket stress.",
      icon: Wallet,
    },
  ];

  return (
    <section
      className="bg-[#0A1128] py-32 relative overflow-hidden text-white"
      id="employees"
    >
      <LiquidGlassBg />

      <div className="container-wide relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div className="order-2 lg:order-1 relative">
            {/* iPhone Mockup */}
            <div className="relative mx-auto w-[260px] sm:w-[280px] lg:w-[320px]">
              <IphoneFrame className="border-white/5 shadow-2xl shadow-[#0048ff]/20">
                <div className="p-0 h-full overflow-hidden">
                  <img
                    src={employeeAppImg}
                    alt="Mobile App Interface"
                    className="w-full h-full"
                  />
                </div>
              </IphoneFrame>

              {/* Liquid Glass Floating notification */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8, x: 20 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                className="liquid-glass absolute -right-12 top-20 p-4 rounded-2xl shadow-xl flex items-center gap-3 border border-white/20 z-30"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500">
                  <CheckCircle size={24} weight="fill" />
                </div>
                <div className="text-sm">
                  <p className="font-bold text-white">Claim Approved</p>
                  <p className="text-white/60">Processed in 0.4s</p>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div {...fadeUp} className="order-1 lg:order-2">
            <span className="text-[#0048ff] text-sm tracking-[0.2em] uppercase font-bold mb-6 block">
              The Employee Reality
            </span>
            <h2 className="text-[40px] md:text-[52px] font-bold mb-8 tracking-tight leading-[1.1]">
              Stop Stressing Your <br />
              <span className="text-[#0048ff]">Most Valuable Asset.</span>
            </h2>
            <p className="text-white/60 text-lg mb-12 leading-relaxed">
              Employees are drowning in complexity. Between insurance approvals,
              network navigation, and rising out-of-pocket costs, healthcare has
              become a source of stress, not a benefit.
            </p>

            <div className="grid sm:grid-cols-2 gap-8">
              {steps.map((item, idx) => (
                <div
                  key={idx}
                  className="group p-6 rounded-2xl transition-all duration-300 hover:bg-white/5 border border-transparent hover:border-white/10 liquid-glass-dark"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-[#0048ff] mb-4 group-hover:bg-[#0048ff] group-hover:text-white transition-all duration-300">
                    <item.icon size={24} />
                  </div>
                  <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                  <p className="text-white/40 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Silent Majority Section - Focus on 100%
function SilentMajoritySection() {
  const cards = [
    {
      title: "Preventive Screenings",
      desc: "Identify risks before they become high-cost clinical interventions.",
      icon: Flask,
    },
    {
      title: "Chronic Care",
      desc: "Active management of lifestyle diseases to maintain workforce health.",
      icon: Pulse,
    },
    {
      title: "Mental Wellbeing",
      desc: "Holistic support for the psychological stressors of modern work.",
      icon: Brain,
    },
    {
      title: "Lifestyle Governance",
      desc: "Rule-based rewards and incentives for healthy employee behavior.",
      icon: Gift,
    },
  ];

  return (
    <section className="bg-white py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-[600px] bg-red-500/5 rounded-full blur-[120px] -translate-y-1/2 opacity-30 pointer-events-none" />

      <div className="container-wide relative z-10">
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <span className="label-text mb-4 block uppercase tracking-[0.2em] font-bold text-[#0048ff]">
            The Silent Majority
          </span>
          <h2 className="heading-section mb-8">
            Manage the 100%. <br /> Not just the 5%.
          </h2>
          <p className="body-text text-xl">
            Traditional models focus on the 5% who are hospitalized. Mentor
            Health focuses on the 100%. We manage the silent majority through
            preventive care before they become high-cost claimants.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {cards.map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ y: -10 }}
              className="p-8 rounded-[32px] bg-[#F8F9FB] border border-[#E5E7EB] hover:border-[#0048ff]/20 transition-all group liquid-glass"
            >
              <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center text-[#0048ff] mb-8 shadow-xl border border-[#0048ff]/10">
                <item.icon size={28} weight="duotone" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-[#11111f]">
                {item.title}
              </h3>
              <p className="text-[#11111f]/50 text-sm leading-relaxed font-medium">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Impact Section - Measurable Results (renamed from CostSection)
function ImpactSection() {
  const benefits = [
    {
      title: "Cost Reduction",
      value: "20-30%",
      desc: "Average reduction in health spend through broker-free insurance and rule-based governance.",
      icon: TrendDown,
    },
    {
      title: "Compliance Rate",
      value: "100%",
      desc: "Automated KYC and Shariah-compliant fund management ensuring total organizational safety.",
      icon: ShieldCheck,
    },
    {
      title: "Admin Efficiency",
      value: "10X",
      desc: "Replace manual bill processing with instant digital settlements and real-time reconciliation.",
      icon: ArrowsClockwise,
    },
  ];

  return (
    <section
      className="bg-white py-32 border-t border-[#E5E7EB] relative overflow-hidden"
      id="impact"
    >
      <div className="absolute bottom-0 left-0 w-full h-[600px] bg-[#0048ff]/5 rounded-full blur-[120px] translate-y-1/2 opacity-20 pointer-events-none" />

      <div className="container-wide relative z-10">
        <div className="max-w-3xl mb-20 text-center mx-auto">
          <span className="label-text mb-4 block text-[#0048ff] font-bold uppercase tracking-widest">
            Financial Impact
          </span>
          <h2 className="heading-section mb-8">
            Superior Infrastructure.
            <br />
            Measurable Outcomes.
          </h2>
          <p className="subheading-section">
            Stop guessing your health benefit ROI. Start measuring it with
            real-time data and automated governance through Mentor Health's
            IaaS.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {benefits.map((item, idx) => (
            <motion.div
              key={idx}
              className="p-10 group rounded-[40px] bg-white border border-[#E5E7EB] transition-all duration-500 hover:shadow-2xl hover:shadow-[#0048ff]/5 liquid-glass"
              whileHover={{ y: -10 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <div className="w-16 h-16 rounded-2xl bg-[#0048ff]/5 flex items-center justify-center text-[#0048ff] mb-8 group-hover:bg-[#0048ff] group-hover:text-white transition-all duration-500 border border-[#0048ff]/10">
                <item.icon size={32} weight="light" />
              </div>
              <p className="text-6xl font-black text-[#11111f] mb-4 tracking-tighter">
                {item.value}
              </p>
              <h3 className="text-xl font-bold mb-4 text-[#0048ff]">
                {item.title}
              </h3>
              <p className="body-text text-sm font-medium leading-relaxed opacity-60">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Solution Section - Premium Dark
function SolutionSection() {
  const architectureNodes = [
    {
      title: "Scalable Infrastructure",
      desc: "Architected to manage 100k+ employees with zero latency.",
      icon: Lightning,
      position: "top-0 -left-12",
      delay: 0.1,
    },
    {
      title: "Bank-Grade Security",
      desc: "HIPAA & GDPR compliant data governance by default.",
      icon: ShieldCheck,
      position: "bottom-12 -right-12",
      delay: 0.3,
    },
    {
      title: "Total Transparency",
      desc: "Every transaction, every claim, visible in real-time.",
      icon: ChartLineUp,
      position: "top-20 -right-8",
      delay: 0.2,
    },
  ];

  return (
    <section className="bg-[#11111f] py-32 md:py-48 relative overflow-hidden">
      {/* Enhanced Background Effects */}
      <LiquidGlassBg />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#0048ff]/30 to-transparent" />

      {/* Kinetic Light Beams */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-[150%] h-px bg-gradient-to-r from-transparent via-[#0048ff]/20 to-transparent -rotate-45 animate-[pulse_5s_infinite]" />
        <div className="absolute bottom-1/4 -right-1/4 w-[150%] h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent -rotate-45 animate-[pulse_7s_infinite]" />
      </div>

      <div className="container-wide relative z-10">
        <div className="text-center mb-24 max-w-[900px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#0048ff] text-sm tracking-[0.3em] uppercase font-bold mb-6 block">
              The Architecture
            </span>
            <h2 className="text-[40px] md:text-[64px] font-bold text-white mb-8 tracking-tighter leading-[1.05]">
              Built for{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0048ff] to-cyan-400">
                Governance.
              </span>
              <br />
              Engineered for Scale.
            </h2>
            <p className="text-white/40 text-xl font-medium max-w-2xl mx-auto leading-relaxed">
              We didn't just build a health app. We built a high-governance
              infrastructure layer that connects every benefit rail into a
              single source of truth.
            </p>
          </motion.div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 md:px-0">
          {/* Main Dashboard Visualization */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative z-10"
          >
            <div className="relative group">
              {/* 3D Depth Rings around the mockup */}
              <div className="absolute inset-[-100px] border border-white/5 rounded-full pointer-events-none opacity-50 group-hover:scale-105 transition-transform duration-1000" />
              <div className="absolute inset-[-60px] border border-white/10 rounded-full pointer-events-none animate-[spin_20s_linear_infinite]" />

              <MacbookFrame className="border-white/10 shadow-[0_0_100px_rgba(0,72,255,0.2)] bg-[#11111f]/80 backdrop-blur-3xl overflow-hidden">
                <div className="relative p-0 overflow-hidden bg-black">
                  <motion.div
                    animate={{
                      y: [0, -20, 0],
                      opacity: [0.9, 1, 0.9],
                    }}
                    transition={{
                      duration: 10,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <img
                      src={architectureDashboardImg}
                      alt="Infrastructure Overview"
                      className="w-full h-auto brightness-110"
                    />
                  </motion.div>

                  {/* Glass Shimmer Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-transparent pointer-events-none" />
                </div>
              </MacbookFrame>
            </div>
          </motion.div>

          {/* Architecture Nodes - Grid on Mobile, Floating on Desktop */}
          <div className="mt-20 lg:mt-0">
            <div className="grid md:grid-cols-2 lg:block gap-6">
              {architectureNodes.map((node, idx) => (
                <motion.div
                  key={idx}
                  className={`relative lg:absolute ${node.position} z-20 w-full lg:w-[240px]`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: node.delay, duration: 0.8 }}
                >
                  <div className="liquid-glass bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-2xl hover:bg-white/10 transition-all group group/node cursor-default">
                    <div className="w-12 h-12 rounded-xl bg-[#0048ff]/20 flex items-center justify-center mb-6 text-[#0048ff] group-hover/node:bg-[#0048ff] group-hover/node:text-white transition-all">
                      <node.icon size={24} weight="bold" />
                    </div>
                    <h4 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">
                      {node.title}
                    </h4>
                    <p className="text-white/40 text-xs leading-relaxed group-hover/node:text-white/70 transition-colors">
                      {node.desc}
                    </p>

                    {/* Connection Line back to center (Desktop Only) */}
                    <div className="hidden lg:block absolute top-1/2 left-full w-24 h-px bg-gradient-to-r from-white/20 to-transparent scale-x-0 group-hover/node:scale-x-100 origin-left transition-transform duration-500 pointer-events-none" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Infrastructure Tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="absolute -bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center z-20"
          >
            <div className="bg-gradient-to-r from-[#0048ff] to-cyan-500 p-[1px] rounded-full shadow-2xl overflow-hidden group">
              <div className="bg-[#11111f] px-8 py-3 rounded-full text-white text-sm font-black tracking-widest whitespace-nowrap flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                INFRASTRUCTURE AS A SERVICE (IaaS)
              </div>
            </div>

            {/* Visual Anchor */}
            <div className="w-px h-16 bg-gradient-to-b from-[#0048ff] to-transparent mt-4 opacity-50" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Executive Approval Section - CEO & CFO Perspectives
// Executive Approval Section - CEO & CFO Perspectives
// function ExecutiveApprovalSection() {
//   const roles = [
//     {
//       role: "CEO PERSPECTIVE",
//       name: "Strategic Governance",
//       quote:
//         "Healthcare is no longer a cost center; it's a strategic asset for talent and operational efficiency. We need infrastructure that scales with our global ambition.",
//       gains: ["Global visibility", "Talent attraction", "Operational scale"],
//       icon: Buildings,
//       theme: "dark",
//     },
//     {
//       role: "CFO PERSPECTIVE",
//       name: "Financial Optimization",
//       quote:
//         "Shift from 'spend and hope' to 'govern and optimize' with full financial transparency and rule-based utilization control.",
//       gains: [
//         "20% cost optimization",
//         "Real-time reconciliation",
//         "Zero broker leakage",
//       ],
//       icon: ChartLineUp,
//       theme: "light",
//     },
//   ];

//   return (
//     <section className="bg-white py-40 relative overflow-hidden" id="executive">
//       {/* Background Decorative Blurs */}
//       <div className="absolute top-1/2 left-0 w-full h-[600px] bg-[#0048ff]/5 rounded-full blur-[120px] pointer-events-none" />

//       <div className="container-wide relative z-10">
//         <div className="flex flex-col lg:flex-row gap-20">
//           <div className="lg:w-1/3">
//             <motion.div
//               initial={{ opacity: 0, x: -20 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               viewport={{ once: true }}
//               transition={{ duration: 0.8 }}
//             >
//               <span className="label-text mb-6 block font-bold tracking-[0.3em] text-[#0048ff]">
//                 BOARDROOM READY
//               </span>
//               <h2 className="text-[44px] md:text-[56px] font-bold tracking-tighter leading-[1] mb-8 text-[#11111f]">
//                 Executive <br /> Approval <br />{" "}
//                 <span className="text-[#0048ff]">Standard.</span>
//               </h2>
//               <p className="text-lg text-[#11111f]/50 font-medium max-w-sm leading-relaxed">
//                 Designed for the leadership. Built for absolute financial and
//                 operational transparency at the highest level.
//               </p>
//             </motion.div>
//           </div>

//           <div className="lg:w-2/3 grid md:grid-cols-2 gap-8 relative">
//             {roles.map((item, i) => (
//               <motion.div
//                 key={i}
//                 initial={{ opacity: 0, y: 30 }}
//                 whileInView={{ opacity: 1, y: 0 }}
//                 viewport={{ once: true }}
//                 transition={{ delay: i * 0.2, duration: 0.6 }}
//                 className={`p-10 md:p-14 rounded-[40px] border flex flex-col transition-all duration-500 hover:-translate-y-4 group ${
//                   item.theme === "dark"
//                     ? "bg-[#0A1128] text-white border-white/10 shadow-2xl shadow-[#0048ff]/20"
//                     : "bg-[#F8F9FB] border-[#E5E7EB] text-[#11111f] shadow-lg shadow-[#11111f]/5"
//                 }`}
//               >
//                 <div
//                   className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-12 transform group-hover:scale-110 transition-transform duration-500 ${
//                     item.theme === "dark"
//                       ? "bg-[#0048ff]/20 text-[#0048ff] border border-white/10"
//                       : "bg-[#0048ff] text-white shadow-xl shadow-[#0048ff]/30"
//                   }`}
//                 >
//                   <item.icon size={32} weight="duotone" />
//                 </div>

//                 <span
//                   className={`text-[10px] font-bold tracking-[0.4em] uppercase mb-4 block ${
//                     item.theme === "dark" ? "text-white/40" : "text-[#0048ff]"
//                   }`}
//                 >
//                   {item.role}
//                 </span>

//                 <h3 className="text-2xl font-bold mb-10 tracking-tight">
//                   {item.name}
//                 </h3>

//                 <div className="relative mb-12">
//                   <span
//                     className={`absolute -top-6 -left-6 text-7xl opacity-10 font-serif leading-none ${
//                       item.theme === "dark" ? "text-white" : "text-[#0048ff]"
//                     }`}
//                   >
//                     “
//                   </span>
//                   <p
//                     className={`text-xl leading-relaxed relative z-10 font-medium ${
//                       item.theme === "dark"
//                         ? "text-white/80"
//                         : "text-[#11111f]/70"
//                     }`}
//                   >
//                     {item.quote}
//                   </p>
//                 </div>

//                 <div
//                   className={`mt-auto pt-10 border-t ${
//                     item.theme === "dark"
//                       ? "border-white/10"
//                       : "border-[#E5E7EB]"
//                   }`}
//                 >
//                   <div className="flex flex-wrap gap-2">
//                     {item.gains.map((gain, j) => (
//                       <span
//                         key={j}
//                         className={`px-5 py-2.5 rounded-xl text-[10px] items-center flex gap-2 font-bold uppercase tracking-widest ${
//                           item.theme === "dark"
//                             ? "bg-white/5 text-white/50 border border-white/5"
//                             : "bg-white text-[#11111f]/50 border border-[#E5E7EB]"
//                         }`}
//                       >
//                         <div
//                           className={`w-1.5 h-1.5 rounded-full ${item.theme === "dark" ? "bg-[#0048ff]" : "bg-emerald-500"}`}
//                         />
//                         {gain}
//                       </span>
//                     ))}
//                   </div>
//                 </div>
//               </motion.div>
//             ))}
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// }

// Benefit Rails Section - Interactive Tab System
function BenefitRailsSection() {
  const rails = [
    {
      id: 1,
      icon: Hospital,
      title: "OPD Rail",
      desc: "Comprehensive outpatient governance and cashless access.",
      details: [
        "Cashless Execution",
        "Real-time Tracking",
        "Dynamic Pricing",
        "Network Management",
      ],
      href: "/rails/opd",
    },
    {
      id: 2,
      icon: Stethoscope,
      title: "IPD Rail",
      desc: "Streamlined inpatient management and hospitalization cover.",
      details: [
        "Pre-auth Automation",
        "Claim Adjudication",
        "Hospital Relations",
        "Case Management",
      ],
      href: "/rails/ipd",
    },
    {
      id: 3,
      icon: Pill,
      title: "Pharmacy Rail",
      desc: "Direct-to-patient medication fulfillment infrastructure.",
      details: [
        "E-prescription Sync",
        "Home Delivery",
        "Formulary Control",
        "Inventory Insights",
      ],
      href: "/rails/pharmacy",
    },
    {
      id: 4,
      icon: Wallet,
      title: "Wallet-Pool Rail",
      desc: "Custom health wallet infrastructure for flexible financing",
      details: [
        "Funding Automation",
        "Multi-wallet Support",
        "Utilization Guardrails",
        "Instant Settlements",
      ],
      href: "/rails/wallet-pool",
    },
    {
      id: 5,
      icon: Receipt,
      title: "Reimbursement Rail",
      desc: "Flexible digital reimbursement governance with full control",
      details: [
        "AI-based bill scanning",
        "Rule-based approvals",
        "Seamless payroll integration",
        "Audit-ready reporting",
      ],
      href: "/rails/reimbursement",
    },
    {
      id: 6,
      icon: CreditCard,
      title: "Health Credit Rail",
      desc: "KYC-governed credit facility with controlled exposure",
      details: [
        "Automated KYC verification",
        "Tiered credit limits",
        "Salary-linked recovery",
        "Usage-only billing",
      ],
      href: "/rails/health-credit",
    },
    {
      id: 7,
      icon: ShieldCheck,
      title: "Insurance Rail",
      desc: "Autonomous corporate insurance governance and transparency",
      details: [
        "Direct-to-Provider",
        "Premium Comparison",
        "AI-Driven Renewals",
        "Digital Governance",
      ],
      href: "/rails/insurance",
    },
    {
      id: 8,
      icon: Gift,
      title: "Zero Budget Rail",
      desc: "Institutional health access with zero company liability",
      details: [
        "Zero Premium Exposure",
        "Negotiated Discounts",
        "Telemedicine Core",
        "Rapid Activation",
      ],
      href: "/rails/zero-budget",
    },
    {
      id: 9,
      icon: Heartbeat,
      title: "Wellness Rail",
      desc: "Measurable prevention and risk profiling infrastructure",
      details: [
        "Risk Profiling",
        "Home-Sampling",
        "Tele-Consult Stack",
        "Measurable ROI",
      ],
      href: "/rails/wellness",
    },
  ];

  return (
    <section className="py-32 overflow-hidden bg-[#F8F9FB] relative">
      <div className="absolute top-0 right-0 w-full h-full bg-[#0048ff]/5 rounded-full blur-[120px] translate-x-1/2 -translate-y-1/2 pointer-events-none" />

      <div className="container-wide relative z-10">
        <div className="text-center mb-8">
          <span className="label-text mb-4 block uppercase tracking-[0.2em] font-bold text-[#0048ff]">
            Benefit Infrastructure
          </span>
          <h2 className="heading-section mb-6">
            Design Your Benefit Plan. Not the Other Way Around.
          </h2>
          <p className="subheading-section max-w-3xl mx-auto">
            Pick what fits your company. Orbit around your goals. One system,
            multiple configurations.
          </p>
        </div>

        <OrbitalBenefitRails railsData={rails} />

        <div className="mt-20 flex flex-wrap justify-center gap-12 lg:gap-24">
          <div className="text-center">
            <p className="text-4xl font-bold text-[#0048ff]">100%</p>
            <p className="text-[10px] uppercase tracking-widest text-[#11111f]/40 font-bold mt-1">
              Transparent
            </p>
          </div>
          <div className="text-center border-l border-r border-[#E5E7EB] px-12 lg:px-24">
            <p className="text-4xl font-bold text-[#0048ff]">Real-time</p>
            <p className="text-[10px] uppercase tracking-widest text-[#11111f]/40 font-bold mt-1">
              Visibility
            </p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-[#0048ff]">Modular</p>
            <p className="text-[10px] uppercase tracking-widest text-[#11111f]/40 font-bold mt-1">
              Adaptability
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Network Section - Nationwide Coverage
function NetworkSection() {
  const stats = [
    { number: "300+", label: "Partner Hospitals" },
    { number: "15,000+", label: "Network Doctors" },
    { number: "100+", label: "Pharmacy Outlets" },
    { number: "1,200+", label: "Lab Locations" },
  ];

  return (
    <section className="bg-white py-32 relative overflow-hidden" id="network">
      <div className="absolute top-0 right-0 w-full h-[600px] bg-[#0048ff]/5 rounded-full blur-[120px] translate-x-1/2 -translate-y-1/2 opacity-30 pointer-events-none" />

      <div className="container-wide relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <span className="label-text mb-4 block uppercase tracking-[0.2em] font-bold text-[#0048ff]">
              Nationwide Network
            </span>
            <h2 className="heading-section mb-8 text-left mx-0 leading-tight">
              Real Cashless Access.
              <br />
              <span className="text-[#0048ff]">Everywhere.</span>
            </h2>
            <p className="body-text text-lg mb-12 opacity-70">
              Urban and semi-urban coverage with digital eligibility
              verification, real-time authorization, and cashless transactions.
              No paper cards. No manual verification calls. One unified digital
              ID.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {stats.map((stat, idx) => (
                <motion.div
                  key={idx}
                  className="p-8 bg-[#F8F9FB] rounded-2xl border border-[#E5E7EB] hover:border-[#0048ff]/30 transition-all group liquid-glass"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <p className="text-4xl font-black text-[#11111f] group-hover:text-[#0048ff] transition-colors tracking-tight">
                    {stat.number}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#11111f]/40 font-bold mt-1">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="relative flex justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotateX: 10 }}
              whileInView={{ opacity: 1, scale: 1, rotateX: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="relative w-full max-w-2xl"
            >
              <div className="relative inline-block w-full">
                <img
                  src={pk}
                  alt="Nationwide Health Network"
                  className="w-full h-auto drop-shadow-[0_20px_60px_rgba(0,0,0,0.15)] opacity-95 brightness-105"
                  style={{
                    filter: "drop-shadow(0 0 30px rgba(0,72,255,0.05))",
                  }}
                />

                {/* Animated Scan Line */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0048ff]/10 to-transparent h-32 w-full z-10 pointer-events-none opacity-50"
                  animate={{ top: ["-30%", "130%"] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                />

                {/* Geographically Refined City Hotspots - Pakistan */}
                {[
                  {
                    name: "Karachi",
                    top: "78%",
                    left: "41%",
                    color: "#0048ff",
                    delay: 0,
                  },
                  {
                    name: "Lahore",
                    top: "45%",
                    left: "73%",
                    color: "#0048ff",
                    delay: 0.5,
                  },
                  {
                    name: "Islamabad",
                    top: "31%",
                    left: "69%",
                    color: "#0048ff",
                    delay: 1.0,
                  },
                  {
                    name: "Peshawar",
                    top: "28%",
                    left: "63%",
                    color: "#3b82f6",
                    delay: 1.5,
                  },
                  {
                    name: "Quetta",
                    top: "54%",
                    left: "37%",
                    color: "#3b82f6",
                    delay: 2.0,
                  },
                  {
                    name: "Multan",
                    top: "52%",
                    left: "63%",
                    color: "#60a5fa",
                    delay: 2.5,
                  },
                  {
                    name: "Faisalabad",
                    top: "43%",
                    left: "68%",
                    color: "#60a5fa",
                    delay: 3.0,
                  },
                ].map((city, idx) => (
                  <div
                    key={idx}
                    className="absolute z-20 group"
                    style={{ top: city.top, left: city.left }}
                  >
                    {/* Multi-layer Ripple Effect */}
                    <div
                      className="w-6 h-6 rounded-full absolute -inset-1.5 opacity-10 group-hover:opacity-30 transition-opacity blur-sm"
                      style={{ backgroundColor: city.color }}
                    />
                    <motion.div
                      className="w-3 h-3 rounded-full absolute inset-0"
                      style={{ backgroundColor: city.color }}
                      animate={{ scale: [1, 3, 1], opacity: [0.6, 0, 0.6] }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: city.delay,
                      }}
                    />
                    <div
                      className="w-3 h-3 rounded-full relative shadow-lg cursor-pointer"
                      style={{
                        backgroundColor: city.color,
                        boxShadow: `0 0 20px ${city.color}CC`,
                      }}
                    />

                    {/* Premium City Label */}
                    <div className="absolute left-7 top-1/2 -translate-y-1/2 pointer-events-none z-30">
                      <div className="bg-[#11111f]/90 backdrop-blur-xl px-4 py-1.5 rounded-2xl text-[10px] font-bold text-white opacity-0 group-hover:opacity-100 translate-x-[-15px] group-hover:translate-x-0 transition-all duration-500 whitespace-nowrap border border-white/10 shadow-2xl flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#0048ff] animate-pulse" />
                        {city.name} Network
                        <span className="text-white/40 font-medium ml-1">
                          Active
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Floating Network Status Box */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  className="absolute top-10 right-0 p-4 rounded-2xl bg-white/80 backdrop-blur-xl border border-white/20 shadow-xl z-20 hidden md:block"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#11111f]/60">
                      System Status
                    </span>
                  </div>
                  <div className="text-lg font-bold text-[#11111f]">
                    Network Active
                  </div>
                  {/* <div className="text-[10px] text-[#11111f]/40 font-medium font-mono">NODE_PK_LIVE_8820</div> */}
                </motion.div>
              </div>
            </motion.div>

            {/* Quick hospital action overlay */}
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute bottom-12 right-0 md:-right-8 p-6 rounded-2xl shadow-2xl border border-white/20 hidden md:block z-20 liquid-glass bg-white/80 backdrop-blur-xl"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0048ff]/10 flex items-center justify-center text-[#0048ff]">
                  <Hospital size={28} weight="bold" />
                </div>
                <div>
                  <p className="text-sm font-black text-[#11111f] tracking-tight">
                    Partner Network Live
                  </p>
                  <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-widest mt-0.5">
                    ● 300+ Hospitals Active
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Data Engine Section - Dark & Premium
function DataEngineSection() {
  const features = [
    {
      icon: Eye,
      title: "Real-time Visibility",
      desc: "Department-level utilization tracking and cost monitoring.",
    },
    {
      icon: ChartLineUp,
      title: "Burn Rate Forecasting",
      desc: "Predictive engine for future health spend allocation.",
    },
    {
      icon: Users,
      title: "Risk Analysis",
      desc: "Deep pattern detection for high-cost health indicators.",
    },
    {
      icon: Gear,
      title: "Policy Optimization",
      desc: "Autonomous adjustment of benefit rules based on data.",
    },
  ];

  return (
    <section className="bg-[#11111f] py-32 relative overflow-hidden">
      {/* Background Ornaments */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#0048ff]/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 opacity-20" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#0048ff]/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 opacity-20" />

      <div className="container-wide relative z-10">
        <div className="text-center mb-20">
          <span className="text-[#0048ff] text-sm tracking-[0.2em] uppercase font-bold mb-6 block">
            One Data Engine
          </span>
          <h2 className="text-[40px] md:text-[56px] font-bold text-white mb-8 tracking-tight max-w-[900px] mx-auto leading-[1.1]">
            Unprecedented Control Over Corporate Health Spend.
          </h2>
          <p className="text-white/50 text-xl max-w-[700px] mx-auto font-medium">
            Insurance, wallet management, reimbursements, and credit - one
            unified platform for leadership governance.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((item, idx) => (
            <motion.div
              key={idx}
              className="bg-white/[0.03] border border-white/10 rounded-[24px] p-8 hover:bg-white/[0.06] transition-all hover:-translate-y-2 group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <div className="w-16 h-16 rounded-2xl bg-[#0048ff]/10 flex items-center justify-center mb-10 group-hover:bg-[#0048ff] transition-all duration-500">
                <item.icon
                  size={32}
                  className="text-[#0048ff] group-hover:text-white"
                  weight="light"
                />
              </div>
              <h4 className="text-xl font-bold text-white mb-4">
                {item.title}
              </h4>
              <p className="text-sm text-white/40 leading-relaxed font-medium">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// AI Section - Smart Governance
function AISection() {
  const aiFeatures = [
    {
      title: "Claims Intelligence",
      items: [
        "Automated PDF reading",
        "Duplicate bill detection",
        "Policy rule enforcement",
        "Fraud pattern flagging",
      ],
      icon: Brain,
    },
    {
      title: "Risk Analytics",
      items: [
        "Chronic case detection",
        "Anomaly spend monitoring",
        "Fraud indicators",
        "Early warning system",
      ],
      icon: ShieldCheck,
    },
    {
      title: "Predictive Planning",
      items: [
        "Burn rate simulators",
        "Premium projections",
        "Pool sustainability",
        "Cost trend detection",
      ],
      icon: ChartLineUp,
    },
  ];

  return (
    <section className="bg-white py-32" id="ai">
      <div className="container-wide">
        <div className="flex flex-col lg:flex-row justify-between items-end mb-16 gap-8">
          <div className="max-w-2xl">
            <span className="label-text mb-4 block">AI Infrastructure</span>
            <h3 className="heading-section">
              Governed by Code.
              <br />
              Strengthened by Intelligence.
            </h3>
          </div>
          <p className="text-lg text-[#11111f]/40 font-medium max-w-sm">
            Moving beyond simple automation to predictive, smart benefit
            lifecycle management.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {aiFeatures.map((feature, idx) => (
            <motion.div
              key={idx}
              className="p-10 rounded-[32px] border border-[#E5E7EB] bg-white hover:border-[#0048ff]/30 transition-all hover:shadow-2xl hover:shadow-[#0048ff]/5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#0048ff]/5 flex items-center justify-center mb-8 text-[#0048ff]">
                <feature.icon size={32} weight="light" />
              </div>
              <h3 className="text-2xl font-bold text-[#11111f] mb-8">
                {feature.title}
              </h3>
              <ul className="space-y-4">
                {feature.items.map((item, itemIdx) => (
                  <li
                    key={itemIdx}
                    className="flex items-center gap-3 text-sm text-[#11111f]/60 font-medium"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0048ff]" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Why HR Section - Employer Experience
function WhyHRSection() {
  const benefits = [
    {
      title: "One System",
      desc: "Consolidate multiple vendor fragmented portals.",
    },
    {
      title: "Single Login",
      desc: "One unified access for employees and admin.",
    },
    {
      title: "Auto Workflows",
      desc: "Automated claim cycles and settlement paths.",
    },
    {
      title: "Digital Tracker",
      desc: "Real-time visibility into every request state.",
    },
    {
      title: "Zero Friction",
      desc: "Significantly reduced employee support tickets.",
    },
    {
      title: "Pre-engagement",
      desc: "Built-in awareness and communication tools.",
    },
  ];

  return (
    <section className="bg-white py-32 border-t border-[#E5E7EB] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-[600px] bg-[#0048ff]/5 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2 opacity-30 pointer-events-none" />

      <div className="container-wide relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div className="order-2 lg:order-1 relative">
            <div className="max-w-[320px] mx-auto">
              <IphoneFrame className="-rotate-3">
                <div className="p-6 bg-[#F0F2F5] h-full">
                  <div className="w-full h-8 bg-[#0048ff] rounded-lg mb-4" />
                  <div className="space-y-4">
                    <div className="w-full h-32 bg-white rounded-xl shadow-sm p-4">
                      <div className="w-12 h-1.5 bg-[#E5E7EB] rounded-full mb-2" />
                      <div className="w-24 h-1.5 bg-[#E5E7EB] rounded-full mb-6" />
                      <div className="flex gap-2">
                        <div className="w-8 h-8 rounded bg-[#F8F9FB]" />
                        <div className="w-8 h-8 rounded bg-[#F8F9FB]" />
                        <div className="w-8 h-8 rounded bg-[#F8F9FB]" />
                      </div>
                    </div>
                    <div className="w-full h-32 bg-white rounded-xl shadow-sm" />
                  </div>
                </div>
              </IphoneFrame>
            </div>

            <motion.div
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-1/2 -right-8 p-6 rounded-2xl shadow-xl border border-white/20 max-w-[200px] z-20 liquid-glass bg-white/60"
            >
              <Users size={32} weight="light" className="text-[#0048ff] mb-4" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#11111f]/40 mb-1">
                HR Admin
              </p>
              <p className="text-sm font-bold text-[#11111f]">
                "Reduced support tickets by 65%"
              </p>
            </motion.div>
          </div>

          <div className="order-1 lg:order-2">
            <span className="label-text mb-4 block uppercase tracking-[0.2em] font-bold text-[#0048ff]">
              For Modern HR
            </span>
            <h2 className="heading-section mb-8 text-left mx-0 leading-tight">
              Why High-Growth Teams
              <br />
              Prefer Mentor Health
            </h2>
            <div className="grid sm:grid-cols-2 gap-x-12 gap-y-10">
              {benefits.map((item, idx) => (
                <div
                  key={idx}
                  className="group p-5 rounded-2xl transition-all border border-transparent hover:border-[#0048ff]/10 hover:bg-[#F8F9FB] liquid-glass"
                >
                  <h4 className="font-bold text-[#11111f] mb-2 group-hover:text-[#0048ff] transition-colors">
                    {item.title}
                  </h4>
                  <p className="text-sm text-[#11111f]/60 font-medium leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-16">
              <Link to="/demo">
                <Button className="h-14 px-10 text-lg font-bold">
                  Request HR Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Calculators Section - Governance Tools
function CalculatorsSection() {
  const calculators = [
    {
      icon: Calculator,
      title: "Quick Savings Estimator",
      desc: "Fast estimate of possible cost optimization for your current workforce size.",
      href: "/calculators/quick",
    },
    {
      icon: ChartLineUp,
      title: "Advanced Model",
      desc: "Comprehensive model with operational and financial breakdown for enterprise.",
      href: "/calculators/advanced",
    },
    {
      icon: CurrencyCircleDollar,
      title: "Premium Estimator",
      desc: "Estimate annual insurance premium based on active benefit structures.",
      href: "/calculators/insurance",
    },
  ];

  return (
    <section className="bg-[#F8F9FB] py-32" id="calculators">
      <div className="container-wide">
        <div className="text-center mb-16">
          <span className="label-text mb-4 block">Calculators Hub</span>
          <h2 className="heading-section mb-6">Governance by the Numbers.</h2>
          <p className="body-text text-lg max-w-2xl mx-auto">
            Quantify your savings and optimize your benefit structure before you
            commit.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {calculators.map((calc, idx) => (
            <Link key={idx} to={calc.href} className="group">
              <motion.div
                className="p-10 rounded-[32px] bg-white border border-[#E5E7EB] h-full flex flex-col items-center text-center transition-all group-hover:border-[#0048ff]/30 group-hover:shadow-2xl group-hover:shadow-[#0048ff]/5 liquid-glass"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="w-20 h-20 rounded-2xl bg-[#0048ff]/5 flex items-center justify-center mb-10 group-hover:bg-[#0048ff] group-hover:text-white transition-all duration-500 text-[#0048ff]">
                  <calc.icon size={40} weight="light" />
                </div>
                <h3 className="text-xl font-bold text-[#11111f] mb-4">
                  {calc.title}
                </h3>
                <p className="text-sm text-[#11111f]/50 font-medium mb-10 leading-relaxed">
                  {calc.desc}
                </p>
                <div className="mt-auto">
                  <span className="text-sm font-bold text-[#0048ff] flex items-center gap-2">
                    Launch Calculator <ArrowRight size={16} weight="bold" />
                  </span>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// Final CTA Section - Infrastructure
function FinalCTASection() {
  return (
    <section className="bg-[#0A1128] py-40 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-[#0048ff]/20 rounded-full blur-[160px] opacity-20" />
      </div>
      <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] bg-[#0048ff]/20 rounded-full blur-[120px]" />

      <div className="container-wide relative z-10">
        <div className="text-center max-w-[900px] mx-auto">
          <h2 className="text-[40px] md:text-[64px] font-bold text-white mb-10 tracking-tight leading-[1.05]">
            Build Your Corporate Health Infrastructure.
          </h2>
          <p className="text-white/50 text-xl mb-16 max-w-2xl mx-auto font-medium">
            Stop managing health benefits annually. Start governing them as a
            strategic financial system.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link to="/register" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-[#0048ff] hover:bg-[#003bce] h-16 px-12 rounded-2xl gap-3 text-lg font-bold shadow-2xl shadow-[#0048ff]/30 transition-all hover:-translate-y-1"
              >
                Start for Free <ArrowRight size={20} weight="bold" />
              </Button>
            </Link>
            <Link to="/demo" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 h-16 px-12 rounded-2xl text-lg font-bold transition-all"
              >
                Request Live Demo
              </Button>
            </Link>
          </div>
          <p className="text-white/20 text-sm mt-12 font-medium tracking-wide">
            corporatebusiness@thementorhealth.com
          </p>
        </div>
      </div>
    </section>
  );
}

// Main HomePage Component
export default function HomePage() {
  return (
    <Layout>
      <HeroSection />
      <WhatIsSection />
      <ProblemSection />
      <EmployeeRealitySection />
      <SilentMajoritySection />
      <ImpactSection />
      {/* <ExecutiveApprovalSection /> */}
      <SolutionSection />
      <BenefitRailsSection />
      <NetworkSection />
      <DataEngineSection />
      <CalculatorsSection />
      <FinalCTASection />
    </Layout>
  );
}
